// Support for restricting postMessage() communication between the embedded
// editor and the parent window that embeds it. See docs/embed-available-transitions.md.

/**
 * Parses VUE_APP_EMBED_PARENT_ORIGINS into a list of exact allowed origins.
 * Empty entries are dropped and whitespace is trimmed. An empty result means
 * "no restriction configured" and callers should fall back to legacy behavior.
 * @returns {string[]}
 */
export const getAllowedEmbedParentOrigins = () => {
    const raw = process.env.VUE_APP_EMBED_PARENT_ORIGINS || '';

    return (
        raw
            .split(',')
            // MessageEvent.origin never has a trailing slash, so normalize
            // entries like `https://dash.spines.com/` to an exact origin.
            .map((origin) => origin.trim().replace(/\/+$/, ''))
            .filter((origin) => origin.length > 0)
    );
};

/**
 * Checks whether an incoming `message` event may be trusted as coming from
 * the embedding parent window.
 * - When `allowedOrigins` is empty, only checks `source === window.parent`
 *   (legacy behavior, kept until the env var is configured everywhere).
 * - When `allowedOrigins` is non-empty, additionally requires `message.origin`
 *   to be one of the allowed origins.
 * @param {MessageEvent} message
 * @param {string[]} allowedOrigins
 * @returns {boolean}
 */
export const isTrustedEmbedParentMessage = (message, allowedOrigins) => {
    if (message.source !== window.parent) return false;

    if (!allowedOrigins.length) return true;

    return allowedOrigins.includes(message.origin);
};

/**
 * Returns the list of targetOrigin values to use for outbound
 * `window.parent.postMessage(data, targetOrigin)` calls.
 * - When `allowedOrigins` is empty, keeps the legacy wildcard behavior.
 * - When `allowedOrigins` is non-empty, posts once per allowed origin instead
 *   of leaking the payload to any parent origin.
 * @param {string[]} allowedOrigins
 * @returns {string[]}
 */
export const getEmbedPostMessageTargetOrigins = (allowedOrigins) => {
    if (!allowedOrigins.length) return ['*'];

    return allowedOrigins;
};
